/**
 * The Secure your brand section's contract with spark-esign: every wire type, the error codes, the
 * guards both transports apply to an answer, and the service interface that the http client
 * (`client.ts`) and the in-memory fake (`fake.ts`) both implement. The source of truth is spark-esign's
 * `docs/api-contract.md` (bitbucket.org/nwt-online/spark-esign, also on Thoth).
 * The wire is snake_case and is kept as is here, so nothing renames a field between the service and
 * the section.
 *
 * Nothing here imports the store or reads `window`: jest and the CT bundle load this module as is.
 */

/** The contract type in every path: `/brands/{brand_id}/contracts/{type}`. */
export const CONTRACT_TYPE = 'license-holder-declaration';

export type ContractStatusValue = 'not_secured' | 'sent' | 'secured';
export type ContractBlocked = 'email_unverified' | 'no_domain';
export type PreviousOutcome = 'declined' | 'expired' | 'cancelled';

/** The GET body, every optional field present (null, never absent), per the shared contract. */
export type ContractStatus = {
  status: ContractStatusValue;
  blocked: ContractBlocked | null;
  email: string;
  sent_at: string | null;
  last_resent_at: string | null;
  signed_at: string | null;
  previous_outcome: PreviousOutcome | null;
  operator_name: string | null;
  signatory_name: string | null;
};

export const CONTRACT_ERROR_CODES = [
  'UNAUTHORIZED',
  'FORBIDDEN',
  'EMAIL_UNVERIFIED',
  'NO_DOMAIN',
  'BRAND_NOT_FOUND',
  'NOT_FOUND',
  'ALREADY_SENT',
  'ALREADY_SECURED',
  'NOT_SENT',
  'NOT_SECURED',
  'DOCUMENT_PENDING',
  'VALIDATION',
  'BRAND_NAME_TOO_LONG',
  'DOMAIN_TOO_LONG',
  'EMAIL_TOO_LONG',
  'RESEND_TOO_SOON',
  'DAILY_LIMIT',
  'DOCUSIGN_FAILED',
  'OWNER_HUB_UNAVAILABLE',
  'INTERNAL',
] as const;

export type ContractErrorCode = (typeof CONTRACT_ERROR_CODES)[number];

export type ContractFieldError = { field: string; code: 'required' | 'too_long'; max: number | null };

/** Every error body: `retry_after` in seconds (429 only), `fields` for VALIDATION only. `message` is for
 *  logs; the section picks its copy by `code`. */
export type ContractError = {
  code: ContractErrorCode;
  message: string;
  retry_after: number | null;
  fields: ContractFieldError[] | null;
};

/** Codes the section never shows copy for: it closes any dialog, refetches and renders the current state (Decision 10). */
export const SILENT_CODES: ReadonlySet<ContractErrorCode> = new Set<ContractErrorCode>([
  'ALREADY_SENT',
  'ALREADY_SECURED',
  'NOT_SENT',
  'NOT_SECURED',
  'EMAIL_UNVERIFIED',
  'NO_DOMAIN',
]);

type Json = Record<string, unknown>;

const isJson = (value: unknown): value is Json => typeof value === 'object' && value !== null && !Array.isArray(value);

const ERROR_CODES: ReadonlySet<string> = new Set<string>(CONTRACT_ERROR_CODES);

/** Whether `value` is the contract's error body: a listed code, a string message, `retry_after` a
 *  number or null, `fields` a list or null. */
export const isContractError = (value: unknown): value is ContractError =>
  isJson(value) &&
  typeof value.code === 'string' &&
  ERROR_CODES.has(value.code) &&
  typeof value.message === 'string' &&
  (value.retry_after === null || typeof value.retry_after === 'number') &&
  (value.fields === null || Array.isArray(value.fields));

const isStatusValue = (value: unknown): value is ContractStatusValue =>
  value === 'not_secured' || value === 'sent' || value === 'secured';

const isBlocked = (value: unknown): value is ContractBlocked | null =>
  value === null || value === 'email_unverified' || value === 'no_domain';

const isPreviousOutcome = (value: unknown): value is PreviousOutcome | null =>
  value === null || value === 'declined' || value === 'expired' || value === 'cancelled';

const isStringOrNull = (value: unknown): value is string | null => value === null || typeof value === 'string';

/**
 * The GET body (also every write's answer) as a {@link ContractStatus}. The three enums are checked,
 * strings and nulls are kept as sent, and only the contract's fields are copied. An absent optional
 * field is a bad shape too: the contract answers null for it and never omits it. A bad shape throws a
 * plain `Error`, so the section reads it as "not the service" and shows its generic line.
 */
export const toContractStatus = (payload: unknown): ContractStatus => {
  if (!isJson(payload)) throw new Error('spark-esign: malformed status');
  const {
    status,
    blocked,
    email,
    sent_at,
    last_resent_at,
    signed_at,
    previous_outcome,
    operator_name,
    signatory_name,
  } = payload;
  if (
    !isStatusValue(status) ||
    !isBlocked(blocked) ||
    typeof email !== 'string' ||
    !isStringOrNull(sent_at) ||
    !isStringOrNull(last_resent_at) ||
    !isStringOrNull(signed_at) ||
    !isPreviousOutcome(previous_outcome) ||
    !isStringOrNull(operator_name) ||
    !isStringOrNull(signatory_name)
  ) {
    throw new Error('spark-esign: malformed status');
  }
  return {
    status,
    blocked,
    email,
    sent_at,
    last_resent_at,
    signed_at,
    previous_outcome,
    operator_name,
    signatory_name,
  };
};

/** The Start and Start over body. An empty `signatory_name` means "the operator signs" (individuals). */
export type StartBody = { operator_name: string; signatory_name: string; language: string };

/** The signed declaration. `filename` is the one `Content-Disposition` names, null when it cannot be read. */
export type ContractDocument = { blob: Blob; filename: string | null };

/** The section's calls. Every method rejects with a {@link ContractError}, or with a plain `Error`
 *  when the answer is not the service's (not configured, the network, a malformed body). */
export type ContractService = {
  fetchStatus: (brandId: string) => Promise<ContractStatus>;
  start: (brandId: string, body: StartBody) => Promise<ContractStatus>;
  resend: (brandId: string) => Promise<ContractStatus>;
  restart: (brandId: string, body: StartBody) => Promise<ContractStatus>;
  download: (brandId: string) => Promise<ContractDocument>;
};

/** Field limits the popup enforces (umbrella: 100 and 100). */
export const OPERATOR_NAME_MAX = 100;
export const SIGNATORY_NAME_MAX = 100;

/** The service's cooldown and cap (service spec defaults), mirrored by the fake. */
export const RESEND_COOLDOWN_MS = 10 * 60 * 1000;
export const DAILY_START_CAP = 3;

/** The OWS locale ids the service writes the email and the declaration in. */
export const ESIGN_LOCALES = ['en', 'cen', 'mx', 'cl', 'co', 'pe', 've', 'arg', 'bra', 'cfr', 'za'] as const;

const ESIGN_LOCALE_IDS: ReadonlySet<string> = new Set<string>(ESIGN_LOCALES);

/** The `language` to send: the app locale id when it is one of the eleven, else `en` (Decision 6). */
export const esignLanguage = (i18nLanguage: string | undefined): string => {
  const id = (i18nLanguage ?? '').toLowerCase().split(/[-_]/)[0];
  return ESIGN_LOCALE_IDS.has(id) ? id : 'en';
};
